import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Card, Spinner } from 'react-bootstrap';
import { FaEdit, FaTrophy, FaBriefcase, FaMoneyBillWave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { updateCareerMilestone } from '../../services/firebase/employeeService';
import DepartmentSelector from '../common/DepartmentSelector';

const EditCareerMilestoneModal = ({ show, onHide, employee, milestone, currentUser, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    effectiveDate: '',
    previousDesignation: '',
    newDesignation: '',
    previousDepartment: '',
    newDepartment: '',
    previousRole: '',
    newRole: '',
    previousSalary: 0,
    newSalary: 0,
    incrementAmount: 0,
    incrementPercentage: 0,
    achievementCategory: 'Award',
    badgeName: '',
    description: '',
  });

  useEffect(() => {
    if (milestone && show) {
      setFormData({
        title: milestone.title || '',
        effectiveDate: milestone.effectiveDate || '',
        previousDesignation: milestone.previousDesignation || '',
        newDesignation: milestone.newDesignation || '',
        previousDepartment: milestone.previousDepartment || '',
        newDepartment: milestone.newDepartment || '',
        previousRole: milestone.previousRole || '',
        newRole: milestone.newRole || '',
        previousSalary: milestone.previousSalary || 0,
        newSalary: milestone.newSalary || 0,
        incrementAmount: milestone.incrementAmount || 0,
        incrementPercentage: milestone.incrementPercentage || 0,
        achievementCategory: milestone.achievementCategory || 'Award',
        badgeName: milestone.badgeName || '',
        description: milestone.description || '',
      });
    }
  }, [milestone, show]);

  const handleSalaryChange = (newVal) => {
    const newSal = parseFloat(newVal) || 0;
    const prevSal = parseFloat(formData.previousSalary) || 0;
    const diff = newSal - prevSal;
    const pct = prevSal > 0 ? ((diff / prevSal) * 100) : 0;

    setFormData(prev => ({
      ...prev,
      newSalary: newSal,
      incrementAmount: diff,
      incrementPercentage: parseFloat(pct.toFixed(2))
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setLoading(true);
    try {
      await updateCareerMilestone(employee.id, milestone.id, formData, currentUser);
      toast.success('Milestone updated successfully');
      if (onSuccess) onSuccess();
      onHide();
    } catch (err) {
      console.error('Error updating milestone:', err);
      toast.error(err.message || 'Failed to update milestone');
    } finally {
      setLoading(false);
    }
  };

  if (!milestone) return null;

  const isSalary = milestone.previousSalary > 0 || milestone.newSalary > 0;
  const isPromotion = !!(milestone.newDesignation || milestone.newDepartment);
  const isAchievement = !!(milestone.badgeName || milestone.achievementCategory);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header closeButton style={{ background: '#1e293b', color: '#ffffff' }}>
        <Modal.Title className="fs-5 d-flex align-items-center gap-2">
          <FaEdit />
          <span>Edit Career Milestone</span>
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="p-4 bg-light">
          <Row className="mb-3 g-3">
            <Col md={8}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark">Milestone Title</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold text-dark">Effective Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.effectiveDate}
                  onChange={(e) => setFormData(p => ({ ...p, effectiveDate: e.target.value }))}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          {isPromotion && (
            <Card className="border-0 shadow-sm mb-3" style={{ borderLeft: '4px solid #2563eb' }}>
              <Card.Body className="p-3">
                <h6 className="fw-bold text-primary mb-3 d-flex align-items-center gap-2">
                  <FaBriefcase /> Designation & Role Details
                </h6>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label className="small text-muted">Previous Designation</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.previousDesignation}
                      onChange={(e) => setFormData(p => ({ ...p, previousDesignation: e.target.value }))}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small fw-bold text-primary">New Designation</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.newDesignation}
                      onChange={(e) => setFormData(p => ({ ...p, newDesignation: e.target.value }))}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small text-muted">Previous Department</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.previousDepartment}
                      onChange={(e) => setFormData(p => ({ ...p, previousDepartment: e.target.value }))}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small fw-bold text-primary">New Department</Form.Label>
                    <DepartmentSelector
                      value={formData.newDepartment}
                      onChange={(val) => setFormData(p => ({ ...p, newDepartment: val }))}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {isSalary && (
            <Card className="border-0 shadow-sm mb-3" style={{ borderLeft: '4px solid #059669' }}>
              <Card.Body className="p-3">
                <h6 className="fw-bold text-success mb-3 d-flex align-items-center gap-2">
                  <FaMoneyBillWave /> Salary Revision
                </h6>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label className="small text-muted">Previous Salary (₹)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.previousSalary}
                      onChange={(e) => setFormData(p => ({ ...p, previousSalary: parseFloat(e.target.value) || 0 }))}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small fw-bold text-success">New Salary (₹)</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.newSalary}
                      onChange={(e) => handleSalaryChange(e.target.value)}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {isAchievement && (
            <Card className="border-0 shadow-sm mb-3" style={{ borderLeft: '4px solid #d97706' }}>
              <Card.Body className="p-3">
                <h6 className="fw-bold text-warning mb-3 d-flex align-items-center gap-2">
                  <FaTrophy /> Recognition & Badge
                </h6>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label className="small">Badge Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.badgeName}
                      onChange={(e) => setFormData(p => ({ ...p, badgeName: e.target.value }))}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold text-dark">Description / Appraisal Remarks</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="bg-white">
          <Button variant="secondary" onClick={onHide} disabled={loading}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={loading} className="px-4 fw-bold">
            {loading ? <Spinner size="sm" animation="border" /> : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditCareerMilestoneModal;
